/**
 * The project accent (`brand.accent` in project.json) drives every highlight in the app: the active tool bar, the
 * pane headers, selected chips and buttons in every tool, the theme cards' hover and press. Production's glass theme
 * writes the same tokens on <html> from the same one colour (© Mighty Spatial Pty Ltd):
 *
 *   --mm-accent         the colour itself (fills, borders, sliders)
 *   --mm-accent-dark    74% with black (the lower end of the filled gradient)
 *   --mm-accent-light   78% with white (icons over the dark glass)
 *   --mm-accent-ink     text and icons ON an accent fill: near-black on a light accent, white on a dark one
 *   --mm-accent-strong  58% with black (text, icons and borders on the tools' white panels)
 *   --mm-accent-soft    12% on white (pale tints)
 *   --mm-accent-grad    the filled gradient (headers, the active bar)
 *
 * glass.css carries the same tokens for the default accent, so the app is whole before the project loads. Status
 * colours (success, error), data palettes and export-file defaults are not accent: they keep their own colours.
 */

/** Production's accent when a project names none. */
export const DEFAULT_ACCENT = '#27e3c4';

const HEX = /^#[0-9a-f]{6}$/i;

/** The project's accent when it is # and six hex digits, else the default. */
export function accentOf(brand: unknown): string {
  const b = typeof brand === 'object' && brand !== null ? (brand as Record<string, unknown>) : {};
  const v = b['accent'];
  return typeof v === 'string' && HEX.test(v) ? v.toLowerCase() : DEFAULT_ACCENT;
}

const channels = (hex: string): [number, number, number] => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((x) => {
    const c = x / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Text on an accent fill: production's rule, dark ink above a luminance of 0.4, white below. */
export const inkOn = (hex: string): string => (luminance(hex) > 0.4 ? '#0c1118' : '#ffffff');

/** The CSS custom properties for one accent. */
export function accentTokens(accent: string): Record<string, string> {
  const acc = HEX.test(accent) ? accent.toLowerCase() : DEFAULT_ACCENT;
  return {
    '--mm-accent': acc,
    '--mm-accent-dark': `color-mix(in srgb, ${acc} 74%, #000)`,
    '--mm-accent-light': `color-mix(in srgb, ${acc} 78%, #fff)`,
    '--mm-accent-ink': inkOn(acc),
    '--mm-accent-strong': `color-mix(in srgb, ${acc} 58%, #000)`,
    '--mm-accent-soft': `color-mix(in srgb, ${acc} 12%, #fff)`,
    '--mm-accent-grad': `linear-gradient(180deg, ${acc}, color-mix(in srgb, ${acc} 74%, #000))`,
  };
}

/** Write the project's accent tokens on <html>, where every stylesheet and inline style reads them. */
export function applyAccent(brand: unknown, root: HTMLElement = document.documentElement): void {
  for (const [k, v] of Object.entries(accentTokens(accentOf(brand)))) root.style.setProperty(k, v);
  // brand.pillWidth (optional): the theme cards' width on a phone, in px; 0 or absent keeps the standard 132.
  const pw = typeof brand === 'object' && brand !== null ? Number((brand as Record<string, unknown>)['pillWidth']) : 0;
  if (pw >= 80 && pw <= 280) root.style.setProperty('--mm-pill-w', `${Math.round(pw)}px`);
  else root.style.removeProperty('--mm-pill-w');
}

/**
 * The accent as a hex colour, for what CSS variables cannot reach (map symbols, canvases, print pages): the value on
 * <html> when it is one, else the default.
 */
export function accentHex(): string {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--mm-accent').trim();
    return HEX.test(v) ? v.toLowerCase() : DEFAULT_ACCENT;
  } catch {
    return DEFAULT_ACCENT;
  }
}

/** --mm-accent-strong as a hex colour (58% with black), for a page with no theme variables, such as a print. */
export function strongHex(hex: string): string {
  if (!HEX.test(hex)) return strongHex(DEFAULT_ACCENT);
  return `#${channels(hex)
    .map((c) =>
      Math.round(c * 0.58)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}
